import { yupResolver } from '@hookform/resolvers/yup';
import { CustomPageType, HeaderCustomization, IOperationResultNoValue, NavLink } from '@ViewModels';
import { css } from 'aphrodite';
import equal from 'fast-deep-equal';
import { FC, useEffect } from 'react';
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useUpdateBlogStyleMutation } from '../../../../../queries/CustomPageStyle/useUpdateBlogStyleMutation';
import { ColorPicker } from '../../../../../web/components-v2/ColorPicker';
import { bs } from '../../../../../web/styles/styles';
import { useAccountContext } from '../../../../components/blogs/AccountContext';
import {
	AppendInputRow,
	InputFormTable,
	InputFormTableRow,
	InputFormTableRowChildren,
} from '../../../../components/blogs/InputFormTable';
import { styleSheet } from '../../../../components/blogs/styles';
import { FontSelect } from '../../../../components/FontSelect/FontSelect';
import { InfoPopover } from '../../../../components/InfoPopover/InfoPopover';
import { LabeledInput } from '../../../../components/LabeledInput/LabeledInput';
import { Column, Row } from '../../../../components/Layout';
import { headerSchema } from './schemas';

const DEFAULT_LINK_VALUES: NavLink = {
	label: '',
	destination: '',
	children: new Array(1).fill({
		label: '',
		destination: '',
	}),
};

const DEFAULT_FORM_VALUES: HeaderCustomization = {
	type: CustomPageType.Header,
	backgroundColor: '',
	dropdown: {
		color: '',
		backgroundColor: '',
		font: {
			fontName: '',
			fontUri: '',
		},
	},
	hoverColor: '',
	overallFont: {
		fontName: '',
		fontUri: '',
	},
	textColor: '',
	mainPageTitle: '',
	menuLinks: new Array(3).fill(DEFAULT_LINK_VALUES),
};

interface HeaderDesignFormProps {
	customContent?: HeaderCustomization;
	onUpdateSuccess: () => void;
	onFormDirty: () => void;
}

export const HeaderDesignForm: FC<HeaderDesignFormProps> = ({ customContent, onFormDirty, onUpdateSuccess }) => {
	const errorMessages = useErrorMessages();
	const { accountId, impersonationContext } = useAccountContext();

	const form = useForm<HeaderCustomization>({
		defaultValues: DEFAULT_FORM_VALUES,
		mode: 'onBlur',
		resolver: yupResolver(headerSchema),
	});
	const {
		control,
		formState: { errors, isDirty },
		handleSubmit,
		register,
		setValue,
		getValues,
		watch,
	} = form;
	const { append, fields, remove } = useFieldArray({ control, name: 'menuLinks' });
	const overallFont = watch('overallFont', {
		fontName: '',
		fontUri: '',
	});
	const dropdownFont = watch('dropdown.font', {
		fontName: '',
		fontUri: '',
	});

	// Watch color values for live updating
	const textColor = watch('textColor');
	const backgroundColor = watch('backgroundColor');
	const hoverColor = watch('hoverColor');
	const dropdownTextColor = watch('dropdown.color');
	const dropdownBackgroundColor = watch('dropdown.backgroundColor');

	const updateHeaderMutation = useUpdateBlogStyleMutation({
		onSuccess: () => {
			onUpdateSuccess();
			toast.success('Saved changes successfully');
		},
		onError: (error: IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const onValid: SubmitHandler<HeaderCustomization> = (data, event) => {
		event.preventDefault();
		event.stopPropagation();

		updateHeaderMutation.mutate({
			id: accountId,
			impersonationContext,
			style: data,
		});
	};

	useEffect(() => {
		if (customContent && !equal(customContent, getValues())) {
			Object.entries(customContent).forEach(entry => {
				if (entry[0] === 'menuLinks' && entry[1].length <= 0) {
					return;
				}
				if (entry[0] === 'menuLinks' && entry[1].length < 3) {
					// links array dynamically generates ui rows
					// minimum of 3 maintains spec
					const newLinksArray = [...entry[1]];
					newLinksArray.length = 3;
					newLinksArray.fill(DEFAULT_LINK_VALUES, entry[1].length);
					setValue('menuLinks', newLinksArray);
					return;
				}
				if (entry[1]) {
					setValue(entry[0] as Extract<keyof HeaderCustomization, string>, entry[1]);
				}
			});
		}
	}, [customContent, getValues, setValue]);

	useEffect(() => {
		if (isDirty) {
			onFormDirty();
		}
	}, [isDirty, onFormDirty]);

	return (
		<FormProvider {...form}>
			<form onSubmit={handleSubmit(onValid)}>
				<Column className={css(styleSheet.gap24)}>
					<Row className={css(styleSheet.titleRow)}>
						<Row className={css(bs.flex, bs.itemsCenter, bs.gap1)}>
							<h1 className={css(styleSheet.titleText)}>Customize Header</h1>
							<InfoPopover message='This form is applicable for both blogs and surveys.' />
						</Row>

						<Column className={css(styleSheet.submitColumn)}>
							<input
								className={css(bs.ctaButton)}
								disabled={updateHeaderMutation.isPending || !!Object.keys(errors).length}
								type='submit'
								value='Save & Update'
							/>
						</Column>
					</Row>
					<Row className={css(styleSheet.gap24)}>
						<Column className={css(bs.flex1, styleSheet.gap24)}>
							<Row className={css(bs.flex, bs.itemsCenter, bs.gap1)}>
								<h2 className={css(styleSheet.subtitleText)}>Font</h2>
								<InfoPopover message='This font will be used as a global default for your blog. You will be able to make further selections for individual components.' />
							</Row>
							<FontSelect
								initialValue={customContent.overallFont}
								onChange={val => setValue('overallFont', val)}
								value={overallFont}
							/>
						</Column>
						<Column className={css(bs.flex1, styleSheet.gap24)}>
							<h2 className={css(styleSheet.subtitleText)}>Banner</h2>
							<LabeledInput
								error={errors?.mainPageTitle}
								label='Main Page Title'
								placeholder='Blogs'
								{...register('mainPageTitle')}
							/>
							<div className={css(styleSheet.gap8)}>
								<label className={css(styleSheet.label)}>Text Color</label>
								<ColorPicker value={textColor || '#000000'} onChange={color => setValue('textColor', color)} />
							</div>
							<div className={css(styleSheet.gap8)}>
								<label className={css(styleSheet.label)}>Background Color</label>
								<ColorPicker
									value={backgroundColor || '#ffffff'}
									onChange={color => setValue('backgroundColor', color)}
								/>
							</div>
							<div className={css(styleSheet.gap8)}>
								<label className={css(styleSheet.label)}>Hover Color</label>
								<ColorPicker value={hoverColor || '#f0f0f0'} onChange={color => setValue('hoverColor', color)} />
							</div>
						</Column>
						<Column className={css(bs.flex1, styleSheet.gap24)}>
							<h2 className={css(styleSheet.subtitleText)}>Dropdown Menu</h2>
							<div className={css(styleSheet.gap8)}>
								<label className={css(styleSheet.label)}>Text Color</label>
								<ColorPicker
									value={dropdownTextColor || '#000000'}
									onChange={color => setValue('dropdown.color', color)}
								/>
							</div>
							<div className={css(styleSheet.gap8)}>
								<label className={css(styleSheet.label)}>Background Color</label>
								<ColorPicker
									value={dropdownBackgroundColor || '#ffffff'}
									onChange={color => setValue('dropdown.backgroundColor', color)}
								/>
							</div>

							<FontSelect
								initialValue={customContent.dropdown?.font}
								onChange={val => setValue('dropdown.font', val)}
								value={dropdownFont}
							/>
						</Column>
						<Column className={css(bs.flex2)} />
					</Row>
					<InputFormTable title='Menu Items'>
						{fields.map((field, index) => (
							<InputFormTableRow<HeaderCustomization>
								collectionName='menuLinks'
								columns={[
									{ fieldName: `label`, placeholder: 'Label' },
									{ fieldName: `destination`, placeholder: 'URL' },
								]}
								key={field.id}
								onDeleteRow={() => remove(index)}
								rowIndex={index}
							>
								<InputFormTableRowChildren<HeaderCustomization, NavLink>
									collectionName='children'
									columns={[
										{ fieldName: 'label', placeholder: 'Label' },
										{ fieldName: 'destination', placeholder: 'URL' },
									]}
									defaultRowValue={{ label: '', destination: '' }}
									parentCollectionName='menuLinks'
									parentRowIndex={index}
								/>
							</InputFormTableRow>
						))}
						<AppendInputRow onAppendRow={() => append(DEFAULT_LINK_VALUES)} />
					</InputFormTable>
				</Column>
			</form>
		</FormProvider>
	);
};
